import { unsafeCSS, html } from "lit";
import { property, query } from "lit/decorators.js";
import { LeafElement } from "../../components/leaf-element.js";
import { appendStylesheet } from "../../utilities/light-dom-styles.js";
import "../../utilities/events.js";
const styles = `/*------------------------------------*\\
    #RESET
\\*------------------------------------*/
/**
 * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/
 */
*,
::slotted(*),
*:before,
*:after {
  box-sizing: border-box;
}

:host *,
:host *::before,
:host *::after {
  box-sizing: inherit;
}

[hidden] {
  display: none !important;
}

/* ------------------------------------*\\
    #TYPOGRAPHY PLACEHOLDERS
\\*------------------------------------ */
:host {
  left: 50%;
  margin-left: -50dvw;
  margin-right: -50dvw;
  position: relative;
  right: 50%;
  width: 100dvw;
}

:host([variant=homepage]) .leaf-c-tcg-hero__container, :host([variant=motif]) .leaf-c-tcg-hero__container,
:host([variant=text-only]) .leaf-c-tcg-hero__container {
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
  width: 100%;
}
@media (min-width: 35rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__container, :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    max-width: 540px;
  }
}
@media (min-width: 48rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__container, :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    max-width: 720px;
  }
}
@media (min-width: 60rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__container, :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    max-width: 960px;
  }
}
@media (min-width: 75rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__container, :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    max-width: 1140px;
  }
}
@media (min-width: 87.5rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__container, :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    max-width: 1320px;
  }
}

/*
=======
Animations
=======
*/
@keyframes fadeIn {
  100% {
    opacity: 1;
  }
}
@keyframes slideIn {
  100% {
    transform: translateX(0);
  }
}
@keyframes slideInFwd {
  100% {
    width: 272px;
    height: 272px;
  }
}
@keyframes slideOutRight {
  100% {
    width: 272px;
    height: 272px;
  }
}
@media (min-width: 481px) {
  @keyframes slideInFwd {
    100% {
      width: 417px;
      height: 417px;
    }
  }
  @keyframes slideOutRight {
    100% {
      width: 417px;
      height: 417px;
    }
  }
}
@media (min-width: 48rem) {
  @keyframes slideInFwd {
    100% {
      width: 330px;
      height: 330px;
    }
  }
  @keyframes slideOutRight {
    100% {
      width: 330px;
      height: 330px;
      transform: translateX(calc(100vw - 45px));
    }
  }
}
@media (min-width: 60rem) {
  @keyframes slideInFwd {
    100% {
      width: 460px;
      height: 460px;
    }
  }
  @keyframes slideOutRight {
    100% {
      width: 460px;
      height: 460px;
      transform: translateX(calc(100vw - 45px));
    }
  }
}
@media (min-width: 75rem) {
  @keyframes slideInFwd {
    100% {
      width: 592px;
      height: 591px;
    }
  }
  @keyframes slideOutRight {
    100% {
      width: 592px;
      height: 591px;
      transform: translateX(calc(100vw - 45px));
    }
  }
}
@media (min-width: 87.5rem) {
  @keyframes slideOutRight {
    100% {
      width: 592px;
      height: 591px;
      transform: translateX(calc(100vw - 120px));
    }
  }
}
@media (min-width: 2200px) {
  @keyframes slideOutRight {
    100% {
      width: 592px;
      height: 591px;
      transform: translateX(calc(100vw - 592px));
    }
  }
}
span.ripple {
  position: absolute;
  border-radius: 50%;
  transform: scale(0);
  animation: ripple 600ms linear;
  background-color: var(--ripple-bg-color);
}

@keyframes ripple {
  to {
    transform: scale(4);
    opacity: 1;
  }
}
:host {
  display: block;
  color: var(--leaf-color-content-knockout);
}
:host ::slotted([slot=heading]) {
  font-size: var(--leaf-font-size-7) !important;
  line-height: var(--leaf-typography-title-default-line-height) !important;
}
@media (min-width: 48rem) {
  :host ::slotted([slot=heading]) {
    font-size: var(--leaf-font-size-9) !important;
    line-height: var(--leaf-typography-title-default-line-height) !important;
  }
}
@media (min-width: 60rem) {
  :host ::slotted([slot=heading]) {
    font-size: var(--leaf-font-size-11) !important;
    line-height: var(--leaf-typography-title-default-line-height) !important;
  }
}
:host ::slotted([slot=content]:not(:empty)) {
  margin-block-start: var(--leaf-spacing-16);
}

:host([background=primary]) {
  --hero-background-color: var(--leaf-color-bg-brand-strong);
}

:host([background=secondary]) {
  --hero-background-color: var(--leaf-color-bg-brand-xstrong);
}

:host([background=alternate]) {
  --hero-background-color: var(--leaf-color-bg-subtle);
  color: var(--leaf-color-content-brand-strong);
}

:host {
  --hero-min-height: 350px;
  --hero-animation-speed-4: 0.4s;
  --hero-animation-speed-6: 0.6s;
  --hero-bottom-offset: 280px;
  --hero-overlay-homepage-size: 717px;
  --hero-image-homepage-size: 816px;
  --hero-image-opt-1-size: 218px;
  --hero-image-opt-2-size: 227px;
  --hero-image-opt-4-size: 296px;
  --hero-image-opt-5-size: 800px;
  --hero-image-opt-7-size: 376px;
  --hero-image-opt-8-size: 258px;
}
@media (min-width: 35rem) {
  :host {
    --hero-overlay-homepage-size: 1447px;
    --hero-image-homepage-size: 2061px;
    --hero-image-opt-2-size: 381px;
    --hero-image-opt-4-size: 384px;
    --hero-image-opt-5-size: 1006px;
    --hero-image-opt-7-size: 529px;
  }
}
@media (min-width: 48rem) {
  :host {
    --hero-image-opt-1-size: 366px;
    --hero-image-opt-7-size: 714px;
    --hero-image-opt-8-size: 334px;
  }
}
@media (min-width: 60rem) {
  :host {
    --hero-image-opt-1-size: 450px;
    --hero-image-opt-2-size: 642px;
    --hero-image-opt-4-size: 730px;
    --hero-image-opt-8-size: 520px;
  }
}

.leaf-c-tcg-hero__overlay-bg {
  display: none;
}

:host([variant=homepage]) {
  --hero-sm-height: 487px;
  --hero-md-height: 535px;
  --hero-lg-height: 664px;
  --hero-overlay-sm-left: 255px;
  --hero-overlay-md-left: 532px;
  --hero-overlay-lg-left: 414px;
  --hero-image-sm-left: 195px;
  --hero-image-md-left: 647px;
  --hero-image-lg-left: 371px;
}
:host([variant=homepage]) ::slotted([slot=meta]) {
  display: none;
}
:host([variant=homepage]) ::slotted([slot=heading]) {
  margin: 0 !important;
}
:host([variant=homepage]) .leaf-c-tcg-hero__container {
  position: relative;
  height: var(--hero-sm-height);
  padding-bottom: 0;
}
@media (min-width: 35rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__container {
    height: var(--hero-md-height);
  }
}
@media (min-width: 60rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__container {
    height: var(--hero-lg-height);
  }
}
:host([variant=homepage]) .leaf-c-tcg-hero__overlay {
  --hero-overlay-sm-height: 298px;
  --hero-overlay-md-height: 328px;
  --hero-overlay-lg-height: 406px;
  position: absolute;
  width: var(--hero-image-homepage-size);
  height: var(--hero-image-homepage-size);
  left: calc(50dvw - (var(--hero-image-sm-left) - (var(--hero-image-homepage-size) - 100dvw) / 2));
  background: var(--hero-background-color);
  border-radius: 50%;
  transform: translate(-50%, -100%);
  transition: var(--hero-animation-speed-6);
  z-index: 1;
}
:host([variant=homepage]) .leaf-c-tcg-hero__overlay.show {
  transform: translate(-50%, calc((var(--hero-image-homepage-size) - var(--hero-sm-height)) * -1));
}
@media (min-width: 35rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__overlay.show {
    left: calc(50dvw - (var(--hero-image-md-left) - (var(--hero-image-homepage-size) - 100dvw) / 2));
    transform: translate(-50%, calc((var(--hero-image-homepage-size) - var(--hero-md-height)) * -1));
  }
}
@media (min-width: 60rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__overlay.show {
    left: calc(50dvw - (var(--hero-image-lg-left) - (var(--hero-image-homepage-size) - 100dvw) / 2));
    transform: translate(-50%, calc((var(--hero-image-homepage-size) - var(--hero-lg-height)) * -1));
  }
}
@media (min-width: 75rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__overlay.show {
    left: 50%;
  }
}
:host([variant=homepage]) .leaf-c-tcg-hero__overlay.post-show {
  width: var(--hero-overlay-homepage-size);
  height: var(--hero-overlay-homepage-size);
  left: calc(50dvw - (var(--hero-overlay-sm-left) - (var(--hero-overlay-homepage-size) - 100dvw) / 2));
  transform: translate(-50%, calc((var(--hero-overlay-homepage-size) - var(--hero-overlay-sm-height)) * -1));
}
@media (min-width: 35rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__overlay.post-show {
    left: calc(50dvw - (var(--hero-overlay-md-left) - (var(--hero-overlay-homepage-size) - 100dvw) / 2));
    transform: translate(-50%, calc((var(--hero-overlay-homepage-size) - var(--hero-overlay-md-height)) * -1));
  }
}
@media (min-width: 60rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__overlay.post-show {
    left: calc(50dvw - (var(--hero-overlay-lg-left) - (var(--hero-overlay-homepage-size) - 100dvw) / 2));
    transform: translate(-50%, calc((var(--hero-overlay-homepage-size) - var(--hero-overlay-lg-height)) * -1));
  }
}
@media (min-width: 75rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__overlay.post-show {
    left: 25%;
  }
}
:host([variant=homepage]) .leaf-c-tcg-hero__text-container {
  --hero-text-sm-left: 350px;
  --hero-text-md-left: 520px;
  --hero-text-lg-left: 650px;
  animation: fadeIn var(--hero-animation-speed-4) forwards;
  animation-delay: 1s;
  max-width: calc(var(--hero-text-sm-left) - var(--leaf-spacing-24));
  opacity: 0;
  position: absolute;
  left: var(--leaf-spacing-24);
  top: var(--leaf-spacing-32);
  width: calc(100dvw - var(--leaf-spacing-48));
  z-index: 4;
}
@media (min-width: 35rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__text-container {
    max-width: calc(var(--hero-text-md-left) - var(--leaf-spacing-24));
    top: var(--leaf-spacing-48);
  }
}
@media (min-width: 60rem) {
  :host([variant=homepage]) .leaf-c-tcg-hero__text-container {
    max-width: calc(var(--hero-text-lg-left) - var(--leaf-spacing-24));
    top: var(--leaf-spacing-80);
  }
}

:host([variant=motif]),
:host([variant=text-only]) {
  background: var(--hero-background-color);
  overflow: hidden;
  position: relative;
}
:host([variant=motif]) .leaf-c-tcg-hero__container,
:host([variant=text-only]) .leaf-c-tcg-hero__container {
  display: grid;
  padding-top: var(--leaf-spacing-32);
}
@media (min-width: 35rem) {
  :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(((100% / 12) * 6) - 12px)) minmax(0, calc(((100% / 12) * 6) - 12px));
  }
}
@media (min-width: 35rem) {
  :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    padding-bottom: var(--leaf-spacing-48);
    padding-top: var(--leaf-spacing-48);
  }
}
@media (min-width: 48rem) {
  :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    min-height: var(--hero-min-height);
  }
}
@media (min-width: 60rem) {
  :host([variant=motif]) .leaf-c-tcg-hero__container,
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    padding-bottom: var(--leaf-spacing-80);
    padding-top: var(--leaf-spacing-80);
  }
}
:host([variant=motif]) .leaf-c-tcg-hero__text-container,
:host([variant=text-only]) .leaf-c-tcg-hero__text-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  grid-column: 1;
  margin-bottom: var(--leaf-spacing-32);
  z-index: 2;
}
@media (min-width: 35rem) {
  :host([variant=motif]) .leaf-c-tcg-hero__text-container,
  :host([variant=text-only]) .leaf-c-tcg-hero__text-container {
    margin-bottom: 0;
  }
}

:host([variant=text-only]) {
  --hero-8-col-width: 850px;
}
@media (min-width: 35rem) {
  :host([variant=text-only]) ::slotted([slot=content]),
  :host([variant=text-only]) ::slotted([slot=meta]) {
    display: block;
    max-width: var(--hero-8-col-width);
  }
}
@media (min-width: 35rem) {
  :host([variant=text-only]) .leaf-c-tcg-hero__container {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc((100% / 12) * 12));
  }
}
:host([variant=text-only]) .leaf-c-tcg-hero__image-container,
:host([variant=text-only]) .leaf-c-tcg-hero__overlay {
  display: none;
}

:host([motif=option-1]) .leaf-c-tcg-hero__overlay {
  background: var(--leafdcws-color-bg-accent-1);
  border-radius: 50%;
  bottom: 0;
  height: var(--hero-image-opt-1-size);
  position: absolute;
  transform: translateX(var(--hero-image-opt-1-size));
  width: var(--hero-image-opt-1-size);
}
@media (min-width: 35rem) {
  :host([motif=option-1]) .leaf-c-tcg-hero__overlay {
    bottom: auto;
    left: 0;
    margin-top: calc(var(--hero-image-opt-1-size) / 2 * -1);
    order: initial;
    top: 50%;
    transform: translateX(100dvw);
    transition: transform var(--hero-animation-speed-6) var(--leafdcws-anim-ease);
  }
}
@media (min-width: 35rem) {
  :host([motif=option-1]) .leaf-c-tcg-hero__overlay.show {
    transform: translateX(calc(50dvw + var(--leaf-spacing-16) + var(--hero-image-opt-1-size)));
  }
}
:host([motif=option-1]) .leaf-c-tcg-hero__image-container {
  align-items: center;
  display: flex;
  height: 100%;
  order: 2;
}
@media (min-width: 35rem) {
  :host([motif=option-1]) .leaf-c-tcg-hero__image-container {
    left: 0;
    order: initial;
    position: absolute;
    top: 0;
    transform: translateX(100dvw);
    transition: transform var(--hero-animation-speed-6) var(--leafdcws-anim-ease);
  }
}
@media (min-width: 35rem) {
  :host([motif=option-1]) .leaf-c-tcg-hero__image-container.show {
    transform: translateX(calc(50dvw + var(--leaf-spacing-16)));
  }
}

@media (min-width: 0) {
  :host([motif=option-2]) .leaf-c-tcg-hero__container {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(((100% / 12) * 9) - 12px)) minmax(0, calc(((100% / 12) * 3) - 12px));
  }
}
@media (min-width: 48rem) and (min-width: 0) {
  :host([motif=option-2]) .leaf-c-tcg-hero__container {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(((100% / 12) * 8) - 12px)) minmax(0, calc(((100% / 12) * 4) - 12px));
  }
}
:host([motif=option-2]) .leaf-c-tcg-hero__overlay {
  background: var(--leaf-color-bg-brand-xstrong);
  border-radius: 50%;
  height: var(--hero-image-opt-2-size);
  position: absolute;
  right: 0;
  transform: translateY(calc(-70% - var(--leaf-spacing-24)));
  width: var(--hero-image-opt-2-size);
  z-index: 2;
}
:host([motif=option-2]) .leaf-c-tcg-hero__overlay::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' fill='none' viewBox='128.44 -367.81 641.15 641.41'%3E%3Cpath d='M194.579 147.539C302.258 288.291 503.463 315.252 643.996 207.74C784.529 100.229 811.135 -101.013 703.456 -241.764C595.777 -382.516 394.56 -409.468 254.039 -301.965C113.518 -194.463 86.8881 6.79692 194.567 147.549L194.579 147.539Z' fill='%233EFFC0'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position-x: calc(var(--hero-image-opt-2-size) / 2);
  background-position-y: calc(var(--hero-image-opt-2-size) / 2);
  border-radius: 50%;
  content: "";
  inset: 0;
  position: absolute;
}
@media (min-width: 35rem) {
  :host([motif=option-2]) .leaf-c-tcg-hero__overlay {
    transform: translate(calc(50dvw + var(--hero-image-opt-2-size) / 3), 100%);
    transition: transform var(--hero-animation-speed-6) ease;
  }
  :host([motif=option-2]) .leaf-c-tcg-hero__overlay::after {
    opacity: 0;
    transition: opacity var(--hero-animation-speed-6) 0.7s ease-out;
  }
}
@media (min-width: 35rem) {
  :host([motif=option-2]) .leaf-c-tcg-hero__overlay.show {
    left: 0px;
    right: initial;
    transform: translate(calc(50dvw + var(--hero-image-opt-2-size) / 3), calc(var(--leaf-spacing-32) * -1));
  }
}
@media (min-width: 60rem) {
  :host([motif=option-2]) .leaf-c-tcg-hero__overlay.show {
    transform: translate(calc(50dvw + var(--hero-image-opt-2-size) / 3), calc(var(--leaf-spacing-64) * -1));
  }
}
@media (min-width: 35rem) {
  :host([motif=option-2]) .leaf-c-tcg-hero__overlay.post-show {
    transition-delay: 0.1s;
    transform: translate(calc(50dvw + var(--hero-image-opt-2-size) / 3 - var(--hero-image-opt-2-size) / 2), calc(var(--hero-image-opt-2-size) / 2 * -1 + var(--leaf-spacing-32) * -1));
  }
  :host([motif=option-2]) .leaf-c-tcg-hero__overlay.post-show::after {
    opacity: 1;
  }
}
@media (min-width: 60rem) {
  :host([motif=option-2]) .leaf-c-tcg-hero__overlay.post-show {
    transform: translate(calc(50dvw + var(--hero-image-opt-2-size) / 3 - var(--hero-image-opt-2-size) / 2), calc(var(--hero-image-opt-2-size) / 2 * -1 + var(--leaf-spacing-64) * -1));
  }
}
:host([motif=option-2]) .leaf-c-tcg-hero__image-container {
  display: flex;
  height: 100%;
  opacity: 0;
  position: absolute;
  top: calc(-10% - var(--leaf-spacing-24));
  right: calc(var(--hero-image-opt-2-size) / 2 * -1);
}
@media (min-width: 35rem) {
  :host([motif=option-2]) .leaf-c-tcg-hero__image-container {
    left: 0px;
    right: initial;
    transform: translateX(calc(50dvw + var(--hero-image-opt-2-size) / 3));
    transition: opacity 0s ease;
    transition-delay: var(--hero-animation-speed-6);
    top: 1rem;
  }
}
:host([motif=option-2]) .leaf-c-tcg-hero__image-container.show {
  opacity: 1;
}

@media (min-width: 35rem) {
  :host([motif=option-3]) .leaf-c-tcg-hero__container {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(((100% / 12) * 8) - 12px)) minmax(0, calc(((100% / 12) * 4) - 12px));
  }
}
@media (min-width: 75rem) and (min-width: 48rem) {
  :host([motif=option-3]) .leaf-c-tcg-hero__container {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(((100% / 12) * 7) - 12px)) minmax(0, calc(((100% / 12) * 5) - 12px));
  }
}
:host([motif=option-3]) .leaf-c-tcg-hero__overlay {
  display: none;
}
:host([motif=option-3]) .leaf-c-tcg-hero__image-container {
  height: 100%;
  left: 50%;
  opacity: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: opacity var(--hero-animation-speed-6) ease;
  transition-delay: var(--hero-animation-speed-4);
  width: 100%;
}
:host([motif=option-3]) .leaf-c-tcg-hero__image-container.show {
  opacity: 1;
}

:host([motif=option-4]) .leaf-c-tcg-hero__overlay {
  background: var(--leafdcws-color-bg-accent-1);
  border-radius: 50%;
  bottom: calc(var(--hero-image-opt-4-size) / 2 * -1);
  height: var(--hero-image-opt-4-size);
  right: var(--hero-image-opt-4-size);
  position: absolute;
  width: var(--hero-image-opt-4-size);
}
@media (min-width: 35rem) {
  :host([motif=option-4]) .leaf-c-tcg-hero__overlay {
    bottom: calc((100% - (var(--hero-image-opt-4-size))) / 2);
    transition: transform var(--hero-animation-speed-4) ease;
    transform: scale(0);
  }
}
@media (min-width: 35rem) {
  :host([motif=option-4]) .leaf-c-tcg-hero__overlay.show {
    left: calc(50dvw + var(--leaf-spacing-24));
    right: initial;
    transform: scale(1);
  }
}
@media (min-width: 35rem) {
  :host([motif=option-4]) .leaf-c-tcg-hero__overlay.post-show {
    bottom: calc((var(--hero-image-opt-4-size) - var(--leaf-spacing-32)) * -1);
    transition: bottom var(--hero-animation-speed-6) ease;
  }
}
:host([motif=option-4]) .leaf-c-tcg-hero__image-container {
  position: absolute;
  bottom: calc(var(--hero-image-opt-4-size) / 2 * -1);
  right: 0;
}
@media (min-width: 35rem) {
  :host([motif=option-4]) .leaf-c-tcg-hero__image-container {
    bottom: 100%;
    left: calc(50dvw + var(--leaf-spacing-24));
    right: initial;
  }
}
@media (min-width: 35rem) {
  :host([motif=option-4]) .leaf-c-tcg-hero__image-container.show {
    bottom: var(--leaf-spacing-32);
    transition: bottom 0.5s ease;
    transition-delay: 0.9s;
  }
}
@media (max-width: 34.999rem) {
  :host([motif=option-4]) .leaf-c-tcg-hero__text-container {
    margin-bottom: calc(var(--hero-image-opt-4-size) / 2 + var(--leaf-spacing-32));
  }
}

:host([motif=option-5]) .leaf-c-tcg-hero__overlay {
  background: var(--leafdcws-color-bg-accent-1);
  border-radius: 50%;
  bottom: calc((var(--hero-image-opt-5-size) - 38px) * -1);
  height: var(--hero-image-opt-5-size);
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  width: var(--hero-image-opt-5-size);
  z-index: 2;
}
@media (min-width: 35rem) {
  :host([motif=option-5]) .leaf-c-tcg-hero__overlay {
    bottom: auto;
    left: 0;
    margin-top: calc(var(--hero-image-opt-5-size) / 2 * -1);
    order: initial;
    top: 50%;
    transform: translateX(100dvw);
    transition: transform var(--hero-animation-speed-6) var(--leafdcws-anim-ease);
  }
}
@media (min-width: 35rem) {
  :host([motif=option-5]) .leaf-c-tcg-hero__overlay.show {
    transform: translateX(calc(50dvw + var(--leaf-spacing-16)));
  }
}
@media (min-width: 35rem) {
  :host([motif=option-5]) .leaf-c-tcg-hero__overlay.post-show {
    transition-delay: 0.1s;
    transform: translateX(calc(50dvw + var(--leaf-spacing-16) - 360px + var(--hero-image-opt-5-size)));
  }
}
:host([motif=option-5]) .leaf-c-tcg-hero__image-container {
  position: absolute;
  bottom: calc((var(--hero-image-opt-5-size) - var(--hero-bottom-offset)) * -1);
  left: 50%;
  transform: translateX(-50%);
}
@media (min-width: 35rem) {
  :host([motif=option-5]) .leaf-c-tcg-hero__image-container {
    bottom: initial;
    left: 0;
    margin-top: calc(var(--hero-image-opt-5-size) / 2 * -1);
    order: initial;
    opacity: 0;
    position: absolute;
    top: 50%;
    transition: opacity 0s ease;
    transition-delay: var(--hero-animation-speed-6);
  }
}
@media (min-width: 35rem) {
  :host([motif=option-5]) .leaf-c-tcg-hero__image-container.show {
    opacity: 1;
    transform: translateX(calc(50dvw + var(--leaf-spacing-16)));
  }
}
@media (max-width: 34.999rem) {
  :host([motif=option-5]) .leaf-c-tcg-hero__text-container {
    margin-bottom: calc(var(--hero-bottom-offset) + var(--leaf-spacing-32));
  }
}

:host([motif=option-6]) {
  --hero-bottom-offset: 190px;
}
:host([motif=option-6]) .leaf-c-tcg-hero__overlay {
  display: none;
}
:host([motif=option-6]) .leaf-c-tcg-hero__image-container {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, calc(100% - var(--hero-bottom-offset)));
  width: 100%;
}
@media (min-width: 35rem) {
  :host([motif=option-6]) .leaf-c-tcg-hero__image-container {
    bottom: initial;
    height: auto;
    left: calc(50% + var(--leaf-spacing-16));
    opacity: 0;
    top: 50%;
    transform: translateY(-50%);
    transition: opacity var(--hero-animation-speed-6) ease;
    transition-delay: var(--hero-animation-speed-4);
    width: -moz-max-content;
    width: max-content;
  }
}
:host([motif=option-6]) .leaf-c-tcg-hero__image-container.show {
  opacity: 1;
}
@media (max-width: 34.999rem) {
  :host([motif=option-6]) .leaf-c-tcg-hero__text-container {
    margin-bottom: calc(var(--hero-bottom-offset) + var(--leaf-spacing-32));
  }
}
@media (min-width: 35rem) {
  :host([motif=option-6]) .leaf-c-tcg-hero__container {
    position: relative;
  }
}

:host([motif=option-7]) .leaf-c-tcg-hero__overlay {
  bottom: calc((var(--hero-image-opt-7-size) - var(--hero-bottom-offset)) * -1);
  height: var(--hero-image-opt-7-size);
  left: calc(var(--hero-image-opt-7-size) / 2);
  opacity: 0;
  position: absolute;
  transform: translateX(-50%);
  width: var(--hero-image-opt-7-size);
  z-index: 2;
}
@media (min-width: 35rem) {
  :host([motif=option-7]) .leaf-c-tcg-hero__overlay {
    bottom: initial;
    left: calc(50% + var(--leaf-spacing-16));
    transform: translateY(-50%);
    top: 50%;
    transition: opacity var(--hero-animation-speed-6) ease;
    transition-delay: var(--hero-animation-speed-4);
  }
}
:host([motif=option-7]) .leaf-c-tcg-hero__overlay.show {
  opacity: 1;
}
:host([motif=option-7]) .leaf-c-tcg-hero__overlay-child-1,
:host([motif=option-7]) .leaf-c-tcg-hero__overlay-child-2,
:host([motif=option-7]) .leaf-c-tcg-hero__overlay-child-3 {
  height: calc(var(--hero-image-opt-7-size) / 2);
  position: absolute;
  width: calc(var(--hero-image-opt-7-size) / 2);
}
:host([motif=option-7]) .leaf-c-tcg-hero__overlay-child-1 {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' fill='none' viewBox='178.51 -193 356.99 357'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M357 164C258.417 164 178.507 84.0903 178.507 -14.4933C178.507 -113.077 258.417 -193 357 -193C455.584 -193 535.493 -113.064 535.493 -14.4933C436.91 -14.4933 357 65.4164 357 164Z' fill='%233EFFC0'/%3E%3C/svg%3E");
  left: 50%;
  transform: translateX(-50%);
}
:host([motif=option-7]) .leaf-c-tcg-hero__overlay-child-2 {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' fill='none' viewBox='178.51 -193 356.99 357'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M357 164C258.417 164 178.507 84.0903 178.507 -14.4933C178.507 -113.077 258.417 -193 357 -193C455.584 -193 535.493 -113.064 535.493 -14.4933C436.91 -14.4933 357 65.4164 357 164Z' fill='%230033FF'/%3E%3C/svg%3E");
  right: 0;
  transform: translateY(-50%) scaleX(-1);
  top: 50%;
}
:host([motif=option-7]) .leaf-c-tcg-hero__overlay-child-3 {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' fill='none' viewBox='178.51 -193 356.99 357'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M357 164C258.417 164 178.507 84.0903 178.507 -14.4933C178.507 -113.077 258.417 -193 357 -193C455.584 -193 535.493 -113.064 535.493 -14.4933C436.91 -14.4933 357 65.4164 357 164Z' fill='%2303CC54'/%3E%3C/svg%3E");
  left: 50%;
  transform: translateX(-50%) rotate(180deg);
  bottom: 0;
}
:host([motif=option-7]) .leaf-c-tcg-hero__image-container {
  bottom: calc((var(--hero-image-opt-7-size) / 2 * -1 - (var(--hero-image-opt-7-size) - var(--hero-bottom-offset)) * -1 - var(--hero-image-opt-7-size) / 4 * -1) * -1);
  left: 0%;
  opacity: 0;
  position: absolute;
  width: calc(var(--hero-image-opt-7-size) / 2);
}
@media (min-width: 35rem) {
  :host([motif=option-7]) .leaf-c-tcg-hero__image-container {
    bottom: initial;
    left: calc(50% + var(--leaf-spacing-16));
    top: 50%;
    transform: translateY(-50%);
    transition: opacity var(--hero-animation-speed-6) ease;
    transition-delay: var(--hero-animation-speed-4);
  }
}
:host([motif=option-7]) .leaf-c-tcg-hero__image-container.show {
  opacity: 1;
}
@media (max-width: 34.999rem) {
  :host([motif=option-7]) .leaf-c-tcg-hero__text-container {
    margin-bottom: calc(var(--hero-bottom-offset) + var(--leaf-spacing-32));
  }
}

:host([motif=option-8]) .leaf-c-tcg-hero__overlay {
  background: var(--leafdcws-color-bg-accent-1);
  border-radius: var(--hero-image-opt-8-size);
  bottom: 0;
  height: var(--hero-image-opt-8-size);
  position: absolute;
  transform: translateX(var(--hero-image-opt-8-size));
  width: var(--hero-image-opt-8-size);
  z-index: 2;
}
@media (min-width: 35rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__overlay {
    bottom: auto;
    left: 0;
    margin-top: calc(var(--hero-image-opt-8-size) / 2 * -1);
    order: initial;
    top: 50%;
    transform: translateX(100dvw);
    transition: transform var(--hero-animation-speed-6) var(--leafdcws-anim-ease);
  }
}
@media (min-width: 35rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__overlay.show {
    transform: translateX(calc(50dvw + var(--leaf-spacing-16) + var(--hero-image-opt-8-size)));
  }
}
@media (min-width: 60rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__overlay.show {
    transform: translateX(calc(50dvw + var(--leaf-spacing-48) + var(--hero-image-opt-8-size)));
  }
}
:host([motif=option-8]) .leaf-c-tcg-hero__image-container {
  align-items: center;
  display: flex;
  height: 100%;
  order: 2;
  z-index: 2;
}
@media (min-width: 35rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__image-container {
    left: 0;
    order: initial;
    position: absolute;
    top: 0;
    transform: translateX(100dvw);
    transition: transform var(--hero-animation-speed-6) var(--leafdcws-anim-ease);
  }
}
@media (min-width: 35rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__image-container.show {
    transform: translateX(calc(50dvw + var(--leaf-spacing-16)));
  }
}
@media (min-width: 60rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__image-container.show {
    transform: translateX(calc(50dvw + var(--leaf-spacing-48)));
  }
}
:host([motif=option-8]) .leaf-c-tcg-hero__overlay-bg {
  background: var(--leaf-color-bg-brand-strong);
  border-radius: var(--hero-image-opt-8-size);
  bottom: 0;
  display: block;
  height: var(--hero-image-opt-8-size);
  left: var(--leaf-spacing-16);
  position: absolute;
  width: calc(var(--hero-image-opt-8-size) * 3);
  z-index: 1;
}
@media (min-width: 35rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__overlay-bg {
    left: 0;
    top: 50%;
    transform: translateX(100dvw);
    transition: transform var(--hero-animation-speed-6) var(--leafdcws-anim-ease);
    transition-delay: 0.1s;
    width: 100%;
  }
}
@media (min-width: 35rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__overlay-bg.show {
    transform: translate(calc(50dvw + var(--leaf-spacing-16)), -50%);
  }
}
@media (min-width: 60rem) {
  :host([motif=option-8]) .leaf-c-tcg-hero__overlay-bg.show {
    transform: translate(calc(50dvw + var(--leaf-spacing-48)), -50%);
  }
}`;
const heroSlotStyles = "tcg-hero {\n  --hero-overlay-homepage-size: 717px;\n  --hero-image-homepage-size: 816px;\n  --hero-image-opt-1-size: 218px;\n  --hero-image-opt-2-size: 227px;\n  --hero-image-opt-4-size: 296px;\n  --hero-image-opt-5-size: 800px;\n  --hero-image-opt-7-size: 376px;\n  --hero-image-opt-8-size: 258px;\n}\n@media (min-width: 35rem) {\n  tcg-hero {\n    --hero-overlay-homepage-size: 1447px;\n    --hero-image-homepage-size: 2061px;\n    --hero-image-opt-2-size: 381px;\n    --hero-image-opt-4-size: 384px;\n    --hero-image-opt-5-size: 1006px;\n    --hero-image-opt-7-size: 529px;\n  }\n}\n@media (min-width: 48rem) {\n  tcg-hero {\n    --hero-image-opt-1-size: 366px;\n    --hero-image-opt-7-size: 714px;\n    --hero-image-opt-8-size: 334px;\n  }\n}\n@media (min-width: 60rem) {\n  tcg-hero {\n    --hero-image-opt-1-size: 450px;\n    --hero-image-opt-2-size: 642px;\n    --hero-image-opt-4-size: 730px;\n    --hero-image-opt-8-size: 520px;\n  }\n}\ntcg-hero [slot]:first-child p:first-child, tcg-hero [slot]:first-child {\n  margin-top: 0;\n}\ntcg-hero .leaf-c-tcg-hero__image {\n  max-inline-size: initial;\n}\ntcg-hero[variant=homepage] {\n  --hero-sm-height: 487px;\n  --hero-md-height: 535px;\n  --hero-lg-height: 664px;\n  --hero-image-sm-left: 195px;\n  --hero-image-md-left: 647px;\n  --hero-image-lg-left: 371px;\n  overflow: hidden;\n}\ntcg-hero[variant=homepage] .leaf-c-tcg-hero__image {\n  border-radius: 50%;\n  position: absolute;\n  width: var(--hero-image-homepage-size);\n  height: var(--hero-image-homepage-size);\n  left: calc(50dvw - (var(--hero-image-sm-left) - (var(--hero-image-homepage-size) - 100dvw) / 2));\n  -o-object-fit: none;\n     object-fit: none;\n  -o-object-position: bottom;\n     object-position: bottom;\n  transform: translate(-50%, -100%);\n  transition: 0.6s;\n  z-index: 0;\n}\ntcg-hero[variant=homepage] .leaf-c-tcg-hero__image.show {\n  transform: translate(-50%, calc((var(--hero-image-homepage-size) - var(--hero-sm-height)) * -1));\n}\n@media (min-width: 35rem) {\n  tcg-hero[variant=homepage] .leaf-c-tcg-hero__image.show {\n    left: calc(50dvw - (var(--hero-image-md-left) - (var(--hero-image-homepage-size) - 100dvw) / 2));\n    transform: translate(-50%, calc((var(--hero-image-homepage-size) - var(--hero-md-height)) * -1));\n  }\n}\n@media (min-width: 60rem) {\n  tcg-hero[variant=homepage] .leaf-c-tcg-hero__image.show {\n    left: calc(50dvw - (var(--hero-image-lg-left) - (var(--hero-image-homepage-size) - 100dvw) / 2));\n    transform: translate(-50%, calc((var(--hero-image-homepage-size) - var(--hero-lg-height)) * -1));\n  }\n}\n@media (min-width: 75rem) {\n  tcg-hero[variant=homepage] .leaf-c-tcg-hero__image.show {\n    left: 50%;\n  }\n}\n@media (min-width: 35rem) {\n  tcg-hero[variant=homepage] .leaf-c-tcg-hero__image {\n    height: var(--hero-image-homepage-size);\n    width: var(--hero-image-homepage-size);\n  }\n}\ntcg-hero[variant=motif][motif=option-1] .leaf-c-tcg-hero__image {\n  width: var(--hero-image-opt-1-size);\n  height: var(--hero-image-opt-1-size);\n  border-radius: 50%;\n}\ntcg-hero[variant=motif][motif=option-2] .leaf-c-tcg-hero__image {\n  width: var(--hero-image-opt-2-size);\n  height: var(--hero-image-opt-2-size);\n  border-radius: 50%;\n}\ntcg-hero[variant=motif][motif=option-3] .leaf-c-tcg-hero__image {\n  -o-object-fit: cover;\n     object-fit: cover;\n  width: 100%;\n  height: 100%;\n}\ntcg-hero[variant=motif][motif=option-4] .leaf-c-tcg-hero__image {\n  width: var(--hero-image-opt-4-size);\n  height: var(--hero-image-opt-4-size);\n  border-radius: 50%;\n}\ntcg-hero[variant=motif][motif=option-5] .leaf-c-tcg-hero__image {\n  width: var(--hero-image-opt-5-size);\n  height: var(--hero-image-opt-5-size);\n  border-radius: 50%;\n}\ntcg-hero[variant=motif][motif=option-7] .leaf-c-tcg-hero__image {\n  border-radius: 50%;\n}\ntcg-hero[variant=motif][motif=option-8] .leaf-c-tcg-hero__image {\n  width: var(--hero-image-opt-8-size);\n  height: var(--hero-image-opt-8-size);\n  border-radius: 50%;\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
class TCGHero extends LeafElement {
  constructor() {
    super();
    this.variant = "homepage";
    this.motif = "option-1";
    this.background = "primary";
    appendStylesheet("tcg-hero-styles", heroSlotStyles);
  }
  get heroImage() {
    return this.querySelector(".leaf-c-tcg-hero__image");
  }
  async updated() {
    await this.updateComplete;
    setTimeout(() => {
      this.heroOverlay.classList.add("show");
      this.heroImageContainer.classList.add("show");
      this.heroOverlayBg.classList.add("show");
      if (this.heroImage) {
        this.heroImage.classList.add("show");
      }
    }, 100);
    setTimeout(() => {
      this.heroOverlay.classList.add("post-show");
    }, 900);
  }
  render() {
    return html`
      <div class="leaf-c-tcg-hero__container">
        <div class="leaf-c-tcg-hero__overlay">
          ${this.motif === "option-7" ? html`<div class="leaf-c-tcg-hero__overlay-child-1"></div>
                <div class="leaf-c-tcg-hero__overlay-child-2"></div>
                <div class="leaf-c-tcg-hero__overlay-child-3"></div>` : ""}
        </div>
        <div class="leaf-c-tcg-hero__image-container">
          <slot name="image"></slot>
        </div>
        <div class="leaf-c-tcg-hero__overlay-bg"></div>
        <div class="leaf-c-tcg-hero__text-container">
          <slot name="meta"></slot>
          <slot name="heading"></slot>
          <slot name="content"></slot>
        </div>
      </div>
    `;
  }
}
TCGHero.styles = unsafeCSS(styles);
__decorateClass([
  property({ reflect: true })
], TCGHero.prototype, "variant", 2);
__decorateClass([
  property()
], TCGHero.prototype, "motif", 2);
__decorateClass([
  property()
], TCGHero.prototype, "background", 2);
__decorateClass([
  query(".leaf-c-tcg-hero__overlay")
], TCGHero.prototype, "heroOverlay", 2);
__decorateClass([
  query(".leaf-c-tcg-hero__overlay-bg")
], TCGHero.prototype, "heroOverlayBg", 2);
__decorateClass([
  query(".leaf-c-tcg-hero__image-container")
], TCGHero.prototype, "heroImageContainer", 2);
export {
  TCGHero
};
//# sourceMappingURL=hero.component.js.map
